import React, { useState, useMemo, useContext, useEffect } from 'react';
import { GlobalCtx } from '../../context/GlobalContext';
import { PetitionPageCtx } from '../../context/PetitionContext';
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
  IconButton,
  Typography,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  TextareaAutosize,
} from '@mui/material';
import { NewSignerForm } from './NewSignerForm';
import axios from 'axios';
import EditIcon from '@mui/icons-material/Edit';
import EditOffIcon from '@mui/icons-material/EditOff';
import { addImgSourceParam, checkUrl } from '../../components/Data';
import { useTranslation } from 'react-i18next';
import { FONT_SIZE, COLORS, URLParams, URL_EXCEPTION_REGEX } from '../../Variables';
import NoInGdprPopup from '../../components/NoInGdprPopup';
import Cookies from 'js-cookie';
import { LANGUAGES, OTD_BASE_URL } from '../../Variables';
import FeedbackAnswersFunc from './FeedbackAnswers';
import AllowDataCheckbox from '../../components/AllowDataCheckbox';
import ConsentMessage from '../../components/constentMessage/ConsentMessage';
import GdprCheckbox from '../../components/GdprCheckbox/GdprCheckbox';

export const PetitionForm = ({ nid, title, personal_msg, thankyou_page, after_sign, sign_button_text, feedback_questions, id, utmCampaign, owner, external, allow_transfer_default }) => {
  const { lang } = useContext(GlobalCtx);
  const { saveUserNameInCookie, userCountryFromIP, userZipFromIP } = useContext(PetitionPageCtx);
  const { t } = useTranslation();

  let timer;
  // the timer var is used to wait 2 seconds after the local email validation
  // before calling the remote validation

  const [signerEmail, setSignerEmail] = useState(''); // Email of the signer (text field value)
  const [errors, setErrors] = useState({}); // Email errors (required, not valid, etc.)
  const [countries, setCountries] = useState([]); // Country list used in the personal info form
  const [zipCodeData, setZipCodeData] = useState([]); // Manages the zip code rules related to the selected country
  const [newGdprConsent, setNewGdprConsent] = useState(''); // For known users that didn't accept GDPR: radio buttons appear with a new request
  const [runLoader, setLoader] = useState(false);
  const [donationURL, setDonationURL] = useState('');
  const [feedback_answers, setFeedbackAnswers] = useState([]); // Feedback answers (when applicable)
  const [feedbackToSend, setFeedbackToSend] = useState(); // Feedback answers to send to the backend
  const [allowDataChecked, setAllowDataChecked] = useState(external ? allow_transfer_default : true);
  const [utmParams, setUtmParams] = useState({});
  const [userInfo, setUserInfo] = useState({
    firstName: '',
    lastName: '',
    country: userCountryFromIP || "",
    zipCode: userZipFromIP || "",
    gdpr: '',
  }); // User info (when applicable); the form fields are in the "NewSignerForm" component

  const [userInfoEditing, setUserInfoEditing] = useState({
    firstName: '',
    lastName: '',
    country: userCountryFromIP || "",
    zipCode: userZipFromIP || "",
    gdpr: true,
  }); // User info (editing mode)

  const [personalMessage, setPersonalMessage] = useState('');

  // Form states:
  // - 0: Email is empty
  // - 1: Email not valid: the email is not syntactically valid (checked by "checkEmail" function)
  // - 2: Email is valid but it has to be verified by the server
  // - 3: Email verified by the server as existing (submit button enabled)
  // - 4: Email verified by the server as new (more info is needed)
  // - 6: Email is new and valid; additional info not completed (submit button disabled)
  // - 7: Email is new and valid; additional info completed (submit button enabled)
  // - 8: Email is existing and valid; user edits additional info; info not completed (submit button disabled)
  // - 9: Email is existing and valid; user edits additional info; info completed (submit button enabled)
  const formStateList = {
    emailIsEmpty: 0,
    emailIsNotValid: 1,
    emailValidToVerify: 2,
    emailIsVerifiedAsExisting: 3,
    emailIsVerifiedAsNew: 4,
    newEmailAdditionalInfoNotCompleted: 6,
    newEmailAdditionalInfoCompleted: 7,
    existingEmailAdditionalInfoNotCompleted: 8,
    existingEmailAdditionalInfoCompleted: 9,
  };

  const [formState, setFormState] = useState(formStateList.emailIsEmpty); // Current form state

  const [editPersonalData, setEditPersonalData] = useState(false);
  // editPersonalData = true if NewSignerForm is visible (the user is new or is editing the personal info)

  const [personalInfoVisibility, setPersonalInfoVisibility] = useState(false);
  // personalInfoVisibility = true if NewSignerForm is visible (used to simplify the conditions checking)

  const [gdprConsentVisibility, setGdprConsentVisibility] = useState(false);
  // gdprConsentVisibility = true if the GDPR radio buttons are visible (used to simplify the conditions checking)

  const [signButtonEnabled, setSignButtonEnabled] = useState(false);
  // signButtonEnabled = true if the Sign button is enabled (used to simplify the conditions checking)

  const [consentMessage, setConsentMessage] = useState(0);
  // consentMessage = 1 if petition_form.data_processing message is shown ("We process your information ...")
  // consentMessage = 2 if petition_form.terms_of_use_acceptance message is shown ("By signing, you accept ...")

  const [submitError, setSubmitError] = useState(''); // Errors from backend after Sign button click

  const gdprCountries = useMemo(
    () => [
      'AT',
      'BE',
      'BG',
      'CY',
      'CZ',
      'DE',
      'DK',
      'EE',
      'ES',
      'FI',
      'FR',
      'GB',
      'GR',
      'HR',
      'HU',
      'IE',
      'IT',
      'LT',
      'LU',
      'LV',
      'MT',
      'NL',
      'PL',
      'PT',
      'RO',
      'SE',
      'SI',
      'SK',
      'NO',
      'LI',
      'IS',
      'AX',
      'AD',
      'BL',
      'GF',
      'PF',
      'MF',
      'MQ',
      'NC',
      'PM',
      'RE',
      'UK',
      'WF',
      'YT'
    ],
    []
  ); // List of country codes for which gdpr applies (not retrieved from the backend to avoid unuseful calls)

  const navigate = useNavigate();

  const passUserName = (fname, editingFname) => {
    if (fname === "" || editingFname !== "") {
      return editingFname;
    }
    return fname;
  }

  useEffect(() => {
    if(!userInfo.country) {
      setUserInfo((prev) => ({...prev, country: userCountryFromIP || "", zipCode: userZipFromIP || ""}))
    }
    if(!userInfoEditing.country) {
      setUserInfoEditing((prev) => ({ ...prev, country: userCountryFromIP, zipCode: userZipFromIP || "" }));
    }
  }, [userCountryFromIP, userZipFromIP])

  useEffect(() => {
    const URL = new URLSearchParams(window.location.search);
    const dr = URL.get('dr');
    const utm_campaign = URL.get('utm_campaign');
    const utm_medium = URL.get('utm_medium');
    const utm_source = URL.get('utm_source');
    const utm_content = URL.get('utm_content');
    const _ref = URL.get('_ref');
    setUtmParams({...utmParams, utm_campaign, utm_medium, utm_source, utm_content, _ref, dr});
  }, [window.location.href]);

  const getEmailFromCookie = () => {
    const defaultEmail = Cookies.get('signerEmail');
    if (defaultEmail && defaultEmail !== '') {
      setSignerEmail(defaultEmail);
    }
  };

  useEffect(() => {
    const allowDataCountries = ['en-af', 'en-au', 'en-ca', 'en-us', 'es-ar', 'es-lat', 'es-mx', 'fr-ca'];
    if(allowDataCountries.includes(lang) && external) {
      setAllowDataChecked(true);
    }
  }, []);
  

  useEffect(() => {
    // When the signer email changes
    setErrors({});
    if (signerEmail.trim() === '') {
      //setErrors({ email: t('petition_form.email_required') }); // The "Email missing" error is not shown
      setFormState(formStateList.emailIsEmpty); // Form state update
    } else {
      // if email in cookie is different from signerEmail, reset newGdprConsent
      if (signerEmail.trim() !== Cookies.get('signerEmail')) {
        setNewGdprConsent('');
      }
      if (checkEmail(signerEmail.trim())) {
        // Local email validation
        // setErrors(''); // Local validation: ok (postponed after timeout)
        setFormState(formStateList.emailValidToVerify); //Form state update
      } else {
        // setErrors({ email: t("petition_form.not_a_valid_email") }); // Local validation: not ok  (postponed after timeout)
        setFormState(formStateList.emailIsNotValid); // Form state update
        clearTimeout(timer); // Stop waiting for remote email validation
      }
    }
    setPersonalInfoVisibility(false);
    setEditPersonalData(false);
    setConsentMessage(0);
    setSignButtonEnabled(false);
    setSubmitError(''); // When something changes, reset the error message
  }, [signerEmail]);

  const checkEmail = (email) => {
    // Local email validation (using a simple regex)
    return /^\S+@\S+\.\S+$/.test(email);
  };

  useEffect(() => {
    // Check the conditions for the NewSignerForm visibility
    let visibility = false;
    if (
      formState === formStateList.emailIsVerifiedAsNew ||
      formState === formStateList.newEmailAdditionalInfoCompleted ||
      formState === formStateList.existingEmailAdditionalInfoCompleted ||
      formState === formStateList.newEmailAdditionalInfoNotCompleted ||
      formState === formStateList.existingEmailAdditionalInfoNotCompleted
    ) {
      // The user is modifying personal info
      visibility = true;
    }
    if (editPersonalData) {
      // The user clicked on the pencil
      visibility = true;
      checkIfAdditionalInfoCompleted(userInfoEditing);
    }
    setPersonalInfoVisibility(visibility);
  }, [formState, editPersonalData]);

  useEffect(() => {
    // Check the conditions for the GDPR radio buttons visibility
    let visibility = false;
    if ((userInfo.gdpr == 'denied' && gdprCountries.indexOf(userInfo.country ?? '') !== -1) || 
        (userInfo.gdpr != 'accepted' && gdprCountries.indexOf(userInfoEditing.country) !== -1 && signerEmail)) {
      // GDPR radio buttons are only visible if previously the user didn't accept GDPR (or he was not asked for consent before and the new country is a gdpr country)
      visibility = true;
    }
    if (personalInfoVisibility && gdprCountries.indexOf(userInfoEditing.country) === -1) {
      // GDPR radio buttons are not visible if the user is editing the personal info and the country is not a GDPR country
      // TODO: redundant?
      visibility = false;
    }
    /* Update: hide GDPR consent for the users that had already accepted
    if (personalInfoVisibility) {
      // The user is editing the personal info
      if (gdprCountries.indexOf(userInfoEditing.country) > -1) {
        visibility = true;
      }
    } else if (!userInfo.gdpr) {
      // The user is not editing the personal info but previously he didn't accept GDPR
      visibility = true;
    }*/
    setGdprConsentVisibility(visibility);
  }, [personalInfoVisibility, userInfoEditing.country, userInfo.gdpr]);

  useEffect(() => {
    // Check the conditions for Sign button enabling
    let enabled = true;
    if (
      !(
        formState === formStateList.emailIsVerifiedAsExisting ||
        formState === formStateList.newEmailAdditionalInfoCompleted ||
        formState === formStateList.existingEmailAdditionalInfoCompleted
      )
    ) {
      enabled = false;
    }
    if (gdprConsentVisibility && newGdprConsent === '') {
      enabled = false;
    }
    if(newGdprConsent === 'no' && gdprConsentVisibility) {
      enabled = false;
    }
    setSignButtonEnabled(enabled);
    if (newGdprConsent !== '') {
      const newUserInfoEditing = {
        ...userInfoEditing,
        gdpr: newGdprConsent === 'yes',
      };
      setUserInfoEditing(newUserInfoEditing);
      checkIfAdditionalInfoCompleted(newUserInfoEditing);
    } else {
      checkIfAdditionalInfoCompleted(userInfoEditing);
    }
  }, [formState, gdprConsentVisibility, newGdprConsent]);

  useEffect(() => {
    let messageCode = 0;
    // Check the conditions for consent message displaying
    if (personalInfoVisibility) {
      // Check the new country
      if (userInfoEditing.country !== '') {
        if (gdprCountries.indexOf(userInfoEditing.country) > -1) {
          messageCode = 1;
        } else {
          messageCode = 2;
        }
      }
    } else {
      // Check the stored country
      if (userInfo.country !== '') {
        if (gdprCountries.indexOf(userInfo.country) > -1) {
          messageCode = 1;
        } else {
          messageCode = 2;
        }
      }
    }
    setConsentMessage(messageCode);
  }, [
    personalInfoVisibility,
    userInfoEditing.country,
    userInfo.country,
    gdprCountries,
  ]);

  useEffect(() => {
    // Gets the country list from the backend at the first rendering
    getCountryList();
  }, []);

  const getCountryList = () => {
    if (countries.length === 0) {
      axios({
        method: 'get',
        url: `${checkUrl()}/api/get_country_list`, // API call
      }).then((response) => {
        // Sets state var
        setCountries(
          response.data
            .map((el) => {
              return {
                code: el.code,
                label: t("country_list." + el.name),
                zip_regex: el.zip_regex,
                zip_code_required: el.zip_code_required,
              };
            })
            .sort((a, b) => {
              return a.label < b.label ? -1 : 1;
            })
        );
      });
    }
  };

  const prepareFeedbackToSend = feedback_questions.map(question => ({
    ...question,
    feedback_answer: feedback_answers[question.id] || null,
  }));

  useEffect(() => {
    setFeedbackToSend(prepareFeedbackToSend);
  }, [feedback_answers]);

  const onChange = (e) => {
    // Handles email text field change
    setSignerEmail(e.target.value); // Updates the state variable
    clearTimeout(timer); // Restarts timer on each email change
    Cookies.remove('_mkto_trk'); //remove munchkin cookie if email is changed
  };

  const handleUserDataChange = (field, value) => {
    // Handles personal info changes
    const newUserInfo = { ...userInfoEditing }; // Object copy
    newUserInfo[field] = value; // Updates the copy
    if (field === 'country') {
      if (!value) {
        newUserInfo['zipCode'] = ''; // If the country is removed, resets the zip code value
      }
      // Checks if the zip code is required
      if (
        value !== '' &&
        countries.find((el) => el.code === value)?.zip_code_required === 1
      ) {
        getZipCodeRules(value); // Gets the zip code rules
      }
    }
    setUserInfoEditing(newUserInfo); // Applies the changes
    checkIfAdditionalInfoCompleted(newUserInfo); // Checks if personal info is completed
    setSubmitError(''); // When something changes, reset the error message
  };

  const checkIfAdditionalInfoCompleted = (currentUserInfo) => {
    if (editPersonalData) {
      // This check is only applicable when personal info is being edited
      // Checks if personal info is completed:
      // first name, last name, and country are mandatory; zip code is requested for some countries
      if (
        (currentUserInfo.firstName && currentUserInfo.firstName !== '') &&
        (currentUserInfo.lastName && currentUserInfo.lastName !== '') &&
        (currentUserInfo.country && currentUserInfo.country !== '') &&
        (!countries.find((el) => el.code === currentUserInfo.country)
          ?.zip_code_required ||
          (currentUserInfo.zipCode && currentUserInfo.zipCode !== ''))
      ) {
        // Personal info completed; updates the form state
        setFormState(
          formState === formStateList.emailIsVerifiedAsNew ||
          formState === formStateList.newEmailAdditionalInfoCompleted ||
          formState === formStateList.newEmailAdditionalInfoNotCompleted
            ? formStateList.newEmailAdditionalInfoCompleted
            : formStateList.existingEmailAdditionalInfoCompleted
        );
      } else {
        // Personal info not completed; updates the form state
        setFormState(
          formState === formStateList.emailIsVerifiedAsNew ||
            formState === formStateList.newEmailAdditionalInfoCompleted ||
            formState === formStateList.newEmailAdditionalInfoNotCompleted
            ? formStateList.newEmailAdditionalInfoNotCompleted
            : formStateList.existingEmailAdditionalInfoNotCompleted
        );
      }
    }
  };

  useEffect(() => {
    // If the zipCodeError var changes, a new check for complete personal info is performed
    getZipCodeRules(userInfoEditing.country);
    checkIfAdditionalInfoCompleted(userInfoEditing);
  }, [editPersonalData]);

  const getZipCodeRules = (newCountry) => {
    // Gets the zip code rules (if they are not yet fetched)
    if (newCountry) {
      // A selected country is needed
      // Checks if the zip code rules list was already downloaded for the selected country
      if (!zipCodeData.find((el) => el.country === newCountry)) {
        // Gets the zip code list from the server
        axios({
          method: 'get',
          url: `${checkUrl()}/api/get_country_zipcode_rule?country=${newCountry}`, // API call
        }).then((response) => {
          const newZipCodeData = [
            ...zipCodeData,
            {
              country: newCountry,
              zip_regex:
                countries.find((el) => el.code === newCountry)?.zip_regex ?? '',
              zip_sub_regex: response.data
                .map((el) => el.zip_sub_regex)
                .filter((el) => el),
            },
          ]; // New zip code rule list added to a copy of the state var
          setZipCodeData(newZipCodeData); // Updates the state var
        });
      }
    }
    return true;
  };

  useEffect(() => {
    // When a new country is selected, gets the zip code rules (if applicable)
    if (userInfoEditing.country !== '') {
      getZipCodeRules(userInfoEditing.country);
    }
  }, [userInfoEditing.country]);

  const checkZipCode = () => {
    // Checks if the zip code is valid according to the selected country's rules
    let valid = false; // Initialization
    if (userInfoEditing.country === '' || userInfoEditing.zipCode === '') {
      // No country selected or no zip code inserted
      valid = true; // No error
    } else {
      // There is a country and a zip code
      const rule = zipCodeData.find(
        (el) => el.country === userInfoEditing.country
      ); // Gets zip code rules for the selected country
      if (rule) {
        rule.zip_regex = countries.find(
          (el) => el.code === userInfoEditing.country
        )?.zip_regex;
        const regEx = new RegExp(rule.zip_regex ?? ''); // General rule
        if (regEx.test(userInfoEditing.zipCode)) {
          // General rule validation ok
          if (rule.zip_sub_regex.length === 0) {
            valid = true; // No sub rules provided: validation is ok
          } else {
            valid = false; // Initialization
            rule.zip_sub_regex.forEach((el) => {
              // Cycles all the sub rules to check if at least one is satisfied
              const subRegEx = new RegExp('^' + el + '(.)*'); // The sub rules are applied to the beginning of the zip code
              if (subRegEx.test(userInfoEditing.zipCode)) {
                // Sub rule validation ok
                valid = true; // Sub rule validation ok
              }
            });
          }
        } else {
          valid = false; // General rule validation not ok
        }
        valid = true; // Alpha version: zipo code validation disabled
      } else {
        valid = true;
      }
    }
  };

  useEffect(() => {
    // Triggered when the zip code changes (or the other vars, used to check the zip code validity, change)
    checkZipCode();
  }, [
    userInfoEditing.zipCode,
    userInfoEditing.country,
    zipCodeData,
    countries,
  ]);

  useEffect(() => {
    const OTD_URL = LANGUAGES.find((item) => item.code === lang?.toLowerCase());
    setDonationURL(`${OTD_BASE_URL}/${OTD_URL?.donateShare}.html`);
  }, []);

  const redirectToLP = (LANDING_PAGE, mParam, id, sid) => {
        if (after_sign === 'don' && !thankyou_page) {
          // if after_sign field is 'don', direct redirect to the donation page after sign
          window.location.href = `${donationURL}${URLParams(mParam, nid ? nid : id, id, sid)}&title=${title.replace(URL_EXCEPTION_REGEX, '-').toLowerCase()}&flowType=don${addImgSourceParam()}${utmCampaign ? `&utm_campaign=${utmCampaign}` : ''}${sid ? `&sid=${sid}` : ""}&fname=${passUserName(userInfo.firstName, userInfoEditing.firstName)}`;
          // window.location.replace(`${window.location.origin}/${lang}/LP3-donate${URLParams(mParam, nid, id)}&title=${title.replace(URL_EXCEPTION_REGEX, '-').toLowerCase()}`);
          return;
        }
        else if (after_sign === 'afd' && !thankyou_page) {
          window.location.href = `${donationURL}${URLParams(mParam, nid ? nid : id, id, sid)}&title=${title.replace(URL_EXCEPTION_REGEX, '-').toLowerCase()}&flowType=don${addImgSourceParam()}${utmCampaign ? `&utm_campaign=${utmCampaign}` : ''}${sid ? `&sid=${sid}` : ""}&fname=${passUserName(userInfo.firstName, userInfoEditing.firstName)}`;
          return;
        }
        else if(after_sign === 'sgc' && thankyou_page) {
          const anyParam = new URL(thankyou_page);
          const currentURL = new URL(window.location.href);
          const allParams = new URLSearchParams(window.location.search);
            if(anyParam.searchParams.toString() || currentURL.searchParams.toString()) {
              window.location.href = `${thankyou_page}?m=${mParam}${utmCampaign ? `&utm_campaign=${utmCampaign}` : ''}${sid ? `&sid=${sid}` : ""}&${allParams.toString()}&fname=${passUserName(userInfo.firstName, userInfoEditing.firstName)}`
            } else {
              window.location.href = `${thankyou_page}?m=${mParam}${utmCampaign ? `&utm_campaign=${utmCampaign}` : ''}${sid ? `&sid=${sid}` : ""}&fname=${passUserName(userInfo.firstName, userInfoEditing.firstName)}`;
            }
        }
        else {
          // if thankyou_page field is empty, redirect to the LP2-share page
          navigate(`${LANDING_PAGE}${mParam}&nid=${id}${sid ? `&sid=${sid}` : ""}&title=${title.replace(URL_EXCEPTION_REGEX, '-').toLowerCase()}&list=${lang}${utmCampaign ? `&utm_campaign=${utmCampaign}` : ''}&fname=${passUserName(userInfo.firstName, userInfoEditing.firstName)}`, { state: utmParams });
          return;
        }
  };

  const handleChangeNewGdpr = (e) => {
    setNewGdprConsent(e.target.value);
  };

  const handleChangeGdprCheckbox = (e) => {
    setNewGdprConsent(e.target.checked ? 'yes' : 'no');
  }

  const handleSignClick = () => {
    if (
      formState === formStateList.emailIsEmpty ||
      formState === formStateList.emailIsNotValid ||
      formState === formStateList.emailValidToVerify ||
      formState === formStateList.newEmailAdditionalInfoNotCompleted ||
      formState === formStateList.existingEmailAdditionalInfoNotCompleted ||
      (gdprConsentVisibility &&
        newGdprConsent !== 'yes' &&
        newGdprConsent !== 'no') ||
      (personalInfoVisibility &&
        (userInfoEditing.firstName === '' ||
          userInfoEditing.lastName === '' ||
          userInfoEditing.country === ''))
    ) {
      setSubmitError(t('petition_form.please_complete_missing_info'));
      return;
    } else {
      setSubmitError('');
    }
    setLoader(true);
    // Sign button clicked
    axios({
      method: 'post',
      url: `${checkUrl()}/api/sign_petition`,
      headers: {
        'Accept-Language': lang,
      },
      data: {
        email: signerEmail.trim(),
        personalInfo: personalInfoVisibility,
        personal_data: {
          first_name: personalInfoVisibility
            ? userInfoEditing.firstName
            : undefined,
          last_name: personalInfoVisibility
            ? userInfoEditing.lastName
            : undefined,
          zip_code: personalInfoVisibility
            ? userInfoEditing.zipCode
            : undefined,
          country: personalInfoVisibility ? userInfoEditing.country : undefined,
          purl: window.location.href,
        },
        pid: id,
        data_consent: gdprConsentVisibility ? userInfoEditing.gdpr : undefined,
        personal_message: personal_msg ? personalMessage : null,
        trk_medium: utmParams.utm_medium,
        trk_campaign: utmParams.utm_campaign,
        trk_content: utmParams.utm_content,
        trk_recruiter: utmParams._ref,
        trk_source: utmParams.utm_source,
        feedback_questions: feedbackToSend,
        allow_data_checked: allowDataChecked,
      }, // API call
    })
      .then((response) => {
        if (response.data.error_code === 0) {
          saveUserNameInCookie(userInfo.firstName, userInfoEditing.firstName);
          setLoader(false);
          Cookies.set('signerEmail', signerEmail.trim(), { expires: 30 });
          redirectToLP(`/LP2-share?m=`, response.data.m, id, response.data.debug.signature);
        } else {
          setLoader(false);
          // Error message from server
          setSubmitError(response.data.error);
        }
      })
      .catch(() => {
        setLoader(false);
        // Uncaught error received from backend
        setSubmitError(t('petition_form.backend_error_on_signature'));
      });
  };

  const askForDataByDr = async () => {
    try {
      const res = await axios.get(
        `${checkUrl()}/api/verifyEmail?dr=${encodeURIComponent(utmParams.dr)}`
      );
      const { first_name, completed, last_name, country, zip, consent, email } =
            res.data;
      if (email) {
        let userInfoFromAPI = {};
        if (first_name && completed) {
          setFormState(formStateList.emailIsVerifiedAsExisting);
          userInfoFromAPI = {
            firstName: first_name,
            lastName: last_name,
            country: country,
            zipCode: zip,
          };
        }
        userInfoFromAPI.gdpr = consent?.toString() ?? ''; //(consent?.toString() ?? '0') !== 'denied';
        setUserInfo(userInfoFromAPI);
        setUserInfoEditing(userInfoFromAPI);
        setSignerEmail(email);
      } else {
        getEmailFromCookie();
      }
    } catch (err) {
      console.log(err.code, err.response.status);
      getEmailFromCookie();
    }
  };

  useEffect(() => {
    if (utmParams.dr) {
      askForDataByDr();
    } else {
      getEmailFromCookie();
    }
  }, [utmParams.dr]);

  useEffect(() => {
    // Triggered when the user changes the email (or the new form state is "emailValidToVerify")
    clearTimeout(timer); // Stops the timer
    if (
      formState === formStateList.emailValidToVerify ||
      formState === formStateList.emailIsNotValid
    ) {
      // The local email validation is ok: waits 2 seconds (to avoid server calls when the user is typing),
      // then sends the email to the backend for user data fetching and, if needed, remote validation
      timer = setTimeout(() => {
        setErrors(''); // Local validation: ok
        if (signerEmail.trim() !== '' && formState === formStateList.emailIsNotValid) {
          setErrors({ email: t('petition_form.not_a_valid_email') }); // Local validation: not ok
        } else {
          axios({
            method: 'get',
            url: `${checkUrl()}/api/verifyEmail?email=${encodeURIComponent(signerEmail.trim())}`, // API call
          }).then((response) => {
            let userInfoFromAPI = {};
            if (response.data.first_name && response.data.completed) {
              // Data received: the email is already in the database
              setFormState(formStateList.emailIsVerifiedAsExisting); // Updates the form state
              userInfoFromAPI = {
                firstName: response.data.first_name,
                lastName: response.data.last_name,
                country: response.data.country,
                zipCode: response.data.zip,
              }; // Updates the personal info
              if(response.data.consent === 'accepted') {
                setNewGdprConsent('yes');
              } else if(response.data.consent === 'denied') {
                setSignButtonEnabled(false);
              }
            } else {
              // New signer or incomplete data
              setFormState(formStateList.emailIsVerifiedAsNew); // Updates the form state
              setEditPersonalData(true);
              userInfoFromAPI = {
                firstName: '',
                lastName: '',
                country: userCountryFromIP || "",
                zipCode: userZipFromIP || "",
              }; // Reset the personal info
            }
            userInfoFromAPI.gdpr = response?.data?.consent?.toString() ?? '';
              //(response?.data?.consent?.toString() ?? '0') !== 'denied';
            setUserInfo(userInfoFromAPI);
            setUserInfoEditing(userInfoFromAPI);
          });
        }
      }, 2000);
      return () => clearTimeout(timer); // Resets the timer
    }
  }, [formState, signerEmail]);

  const handlePersonalData = () => {
    // The signer clicked on the pencil button
    setEditPersonalData(true); // Updates editPersonalData var
    getCountryList();
    getZipCodeRules(userInfo.country);
  };

  const stopHandlePersonalData = () => {
    setEditPersonalData(false);
    setFormState(formStateList.emailIsVerifiedAsExisting);
  };

  /*const handleChange = event => {
        setNewGdprConsent(event.target.value);
    };*/

  function capitalizeFirstLetter(value) {
    if (value !== "") {
      return value[0] + value.slice(1);
    } else {
      return value;
    }
  };

  const renderAllowDataCheckbox = () => {
    if(external) {
      return <AllowDataCheckbox 
      lang={lang} 
      label_content={t('petition_form.allow_data_transfer', 
        { first_name: owner?.first_name, 
          last_name: owner?.last_name 
        })
      } 
      onCheckedChange={
        (newChecked) => setAllowDataChecked(newChecked)
      } 
      allow_transfer_default={allow_transfer_default} />
    }
  };

  return (
    <>
      <TextField
        variant={'outlined'}
        label={
          <Typography sx={{ fontSize: FONT_SIZE.medium }}>
            {t('petition_form.email_placeholder')}
          </Typography>
        }
        sx={{ marginBottom: '10px' }}
        onChange={onChange}
        value={signerEmail || ''}
        error={!!errors.email}
        helperText={errors.email ?? ''}
        InputProps={
          formState === formStateList.emailIsVerifiedAsExisting
            ? {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={t('Edit personal data')}
                    onClick={handlePersonalData}
                    edge="end"
                  >
                    <EditIcon />
                  </IconButton>
                </InputAdornment>
              ),
              style: { fontSize: FONT_SIZE.medium }
            }
            : (formState === formStateList.existingEmailAdditionalInfoCompleted || 
              formState === formStateList.existingEmailAdditionalInfoNotCompleted ?
                    {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={t('Undo edit personal data')}
                            onClick={stopHandlePersonalData}
                            edge="end"
                          >
                            <EditOffIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                      style: { fontSize: FONT_SIZE.medium }
                    }
                :  { style: { fontSize: FONT_SIZE.medium } }              
              )
        }
        inputProps={{ 'data-testid': 'form-email' }}
        className="petition__form-email 111111111"
      />
      {personal_msg && (
        <TextareaAutosize
          minRows={5}
          aria-label="maximum height"
          placeholder={t('petition_form.personal_msg_placeholder')}
          style={{
            fontSize: FONT_SIZE.medium,
            margin: '5px 0 15px',
            padding: '16px 14px',
            borderRadius: '4px',
            border: '1px solid #ccc',
          }}
          onChange={(e) => setPersonalMessage(e.target.value)}
        />
      )}
      {formState === formStateList.emailValidToVerify ? (
        <div style={{ margin: 'auto', fontSize: '1em' }}>
          <CircularProgress />
        </div>
      ) : null}
      {personalInfoVisibility ? (
        <NewSignerForm
          firstName={userInfoEditing.firstName}
          lastName={userInfoEditing.lastName}
          country={userInfoEditing.country}
          zipCode={userInfoEditing.zipCode}
          newUser={
            formState === formStateList.emailIsVerifiedAsNew ||
            formState === formStateList.additionalInfoCompleted
          }
          gdpr={userInfoEditing.gdpr}
          countries={countries}
          gdprCountries={gdprCountries}
          handleUserDataChange={handleUserDataChange}
          lang={lang}
          personal_msg={personal_msg}
        />
      ) : null}
      {gdprConsentVisibility && <GdprCheckbox inputName="gdpr" type="checkbox" onChange={handleChangeGdprCheckbox} />}
      <FeedbackAnswersFunc feedback_questions={feedback_questions} setFeedbackAnswers={setFeedbackAnswers} />
      {newGdprConsent === 'no' && gdprConsentVisibility ? <NoInGdprPopup /> : null}
      {/*runLoader && <ButtonLoader />*/}
        {renderAllowDataCheckbox()}
      <Button
        aria-label="sign"
        color={lang === 'hazteoir' ? "warning" : "primary"}
        variant="contained"
        disabled={!signButtonEnabled || runLoader}
        onClick={handleSignClick}
        sx={{
          fontSize: FONT_SIZE.default,
          backgroundColor:
            lang === 'hazteoir' ? (runLoader ? COLORS.hoDarkerOrange : COLORS.hoOrange) : (runLoader ? COLORS.cgoDarkerBlue : COLORS.cgoBlue),
          marginBottom: '16px',
          "&.Mui-disabled": {
            background: (runLoader ? (lang === 'hazteoir' ? COLORS.hoOrangeDisabled : COLORS.cgoBlueDisabled) : undefined),
            color: (runLoader ? (lang !== 'hazteoir' ? COLORS.lightGreyColor : undefined) : undefined)
          },
          textTransform: 'none'
        }}
      >
        {sign_button_text ? sign_button_text : capitalizeFirstLetter(runLoader ? t('petition_page.signing') : t('petition_form.sign_this_petition'))  }
        {/* {t('petition_form.sign_this_petition')} */}
      </Button>
      {submitError !== '' ? (
        <Typography
          variant="caption"
          display="block"
          style={{ color: 'red', backgroundColor: 'yellow' }}
        >
          {submitError}
        </Typography>
      ) : null}
      <ConsentMessage 
      knownCountry={userInfoEditing.country} 
      newCountry={gdprCountries.indexOf(userInfoEditing.country)} 
      newCountryCode={userInfoEditing.country}
      gdprCountry={gdprCountries.indexOf(userInfoEditing.country)} 
      gdprConsentVisibility={gdprConsentVisibility} 
      email={signerEmail} 
      formState={formState} />
    </>
  );
};
